import { inject, injectable } from "@launchtray/tsyringe-async";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { ModuleFlowExecutor } from "../../../composition/module/ModuleFlowExecutor";
import { ILogger } from "../../../logger/ILogger";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { HourlyBonusModule } from "./HourlyBonusModule";
import { HourlyBonusPopupScene, HourlyBonusSceneOptions } from "./mainHourlyBonus/HourlyBonusPopupScene";
import { HourlyBonusState } from "./mainHourlyBonus/HourlyBonusState";
import { IFortuneWheelModel } from "./model/IFortuneWheelModel";
import { IConnectionModel } from "../../../networking/IConnectionModel";
import { CancellationTokenSource } from "../../../../shared/async/CancellationTokenSource";
import { nameof } from "../../../../shared/bindable-data";
import { CancellationToken } from "async/CancellationToken";
import { HourlyBonusExecutionOptions, HourlyBonusType } from "./HourlyBonusExecutionOptions";
import { Viewable } from "by/gamefactory/scene/viewable/Viewable";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { CheckoutHourlyBonusPopupScene, CheckoutHourlyBonusSceneOptions } from "./checkouthourlybonus/view/CheckoutHourlyBonusPopupScene";
import { CheckOutHourlyBonusPopupSceneFactory, HourlyBonusSceneFactory } from "./HourlyBonusFactories";
import { CheckoutHourlyBonusResourcePackage } from "./checkouthourlybonus/resources/CheckoutHourlyBonusResourcePackage";
import { DicesResourcePackage } from "../../common/resources/DicesResourcePackage";
import { CheckoutsTooltipResourcePackage } from "../../common/resources/CheckoutsTooltipResourcePackage";

@injectable()
export class HourlyBonusModuleFlowExecutor extends ModuleFlowExecutor<HourlyBonusModule> {
    private readonly sceneManager: SceneManager;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly connectionModel: IConnectionModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    private checkoutHourlyBonusResources: CheckoutHourlyBonusResourcePackage;
    private dicesResources: DicesResourcePackage;
    private checkoutsTooltipResources: CheckoutsTooltipResourcePackage;

    private readonly cancellationTokenSource: CancellationTokenSource;

    public constructor(
        sceneManager: SceneManager,
        @inject("IFortuneWheelModel") fortuneWheelModel: IFortuneWheelModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.sceneManager = sceneManager;
        this.fortuneWheelModel = fortuneWheelModel;
        this.connectionModel = connectionModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;

        this.cancellationTokenSource = new CancellationTokenSource();
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChanged, this);
    }

    public async execute<T extends HourlyBonusType>(module: HourlyBonusModule, options?: HourlyBonusExecutionOptions<T>): Promise<void> {
        const cancellationToken = this.cancellationTokenSource.token;
        try {
            if (options == null) {
                throw new Error("Unable to open HourlyBonus scene without options");
            }

            this.asyncOperationNotification.show();
            let scene: Viewable<void> = null;
            switch (options.type) {
                case HourlyBonusType.MAIN:
                    scene = await this.buildMainScene(module, options as HourlyBonusExecutionOptions<HourlyBonusType.MAIN>, cancellationToken);
                    break;
                case HourlyBonusType.CHECKOUT:
                    scene = await this.buildCheckoutScene(module, options as HourlyBonusExecutionOptions<HourlyBonusType.CHECKOUT>, cancellationToken);
                    this.checkoutHourlyBonusResources = new CheckoutHourlyBonusResourcePackage();
                    this.dicesResources = new DicesResourcePackage();
                    this.checkoutsTooltipResources = new CheckoutsTooltipResourcePackage();
                    await this.checkoutHourlyBonusResources.load();
                    await this.dicesResources.load();
                    await this.checkoutsTooltipResources.load();
                    break;
                default:
                    throw new Error(`Unable to build hourly bonus scene, unhandled scene type: ${options.type}`);
            }

            this.asyncOperationNotification.hide();
            if (cancellationToken.isCancellationRequested) {
                scene.view?.destroy();
                return;
            }

            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on execution HourlyBonusModule flow: ", e);
        }
        finally {
            if (this.checkoutHourlyBonusResources?.loaded) {
                this.checkoutHourlyBonusResources.destroy();
            }
            if (this.dicesResources?.loaded) {
                this.dicesResources.destroy();
            }
            if (this.checkoutsTooltipResources?.loaded) {
                this.checkoutsTooltipResources.destroy();
            }
            this.cancellationTokenSource.dispose();
            this.connectionModel.propertyChanged.remove(this.onConnectionModelPropertyChanged, this);
            this.asyncOperationNotification.hide();
        }
    }

    private async buildMainScene(module: HourlyBonusModule, options: HourlyBonusExecutionOptions<HourlyBonusType.MAIN>, cancellationToken: CancellationToken): Promise<HourlyBonusPopupScene> {
        const sceneOptions: HourlyBonusSceneOptions = {
            state: options.args,
            cancellationToken
        };

        if (sceneOptions.state == null) {
            throw new Error("Unable to open HourlyBonus scene without state");
        }

        if (sceneOptions.state === HourlyBonusState.PIGGY) {
            await this.fortuneWheelModel.piggyWheelModel.value.loadPiggyWheelStatus();
        } else {
            await this.fortuneWheelModel.updateFortuneWheelStatus()
            await this.fortuneWheelModel.goldenWheelModel.value.loadGoldenWheelStatus();
        }

        const factory: HourlyBonusSceneFactory = await module.resolve("HourlyBonusSceneFactory");
        const scene = await factory(sceneOptions);
        return scene;
    }

    private async buildCheckoutScene(module: HourlyBonusModule, options: HourlyBonusExecutionOptions<HourlyBonusType.CHECKOUT>, cancellationToken: CancellationToken): Promise<CheckoutHourlyBonusPopupScene> {
        const sceneOptions: CheckoutHourlyBonusSceneOptions = {
            isWheel: options.args.isWheel,
            cancellationToken
        };

        const factory: CheckOutHourlyBonusPopupSceneFactory = await module.resolve("CheckOutHourlyBonusPopupSceneFactory");
        const scene = await factory(sceneOptions);

        if (sceneOptions.isWheel && this.firstTimeExperienceModel.tooltipsInfo.value.hourlyBonusWheelIntro) {
            await this.firstTimeExperienceModel.sendShownTooltip({ tooltips: { hourlyBonusWheelIntro: false } });
            await this.firstTimeExperienceModel.getFirstTimeExperience();
        }
        if (!sceneOptions.isWheel && this.firstTimeExperienceModel.tooltipsInfo.value.hourlyBonusRollsIntro.value) {
            await this.firstTimeExperienceModel.sendShownTooltip({ tooltips: { hourlyBonusRollsIntro: false } });
            await this.firstTimeExperienceModel.getFirstTimeExperience();
        }
        return scene;
    }

    private onConnectionModelPropertyChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug("HourlyBonusModule cancellation requested, reason: disconnect");
                    this.cancellationTokenSource.cancel();
                }
                break;
        }
    }
}